import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Link,
  Select,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grid,
  Switch,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../Redux/Store/Hooks";
import { bookletRequestRead } from "../../../Redux/Slices/Booklet/Admin/BookletRequest/BookletRequestRead";
import { useLocation, useNavigate } from "react-router-dom";
import { BookletCartItem } from "./IMy_request_Details";
import { studyMaterialRequestCreate } from "../../../Redux/Slices/Booklet/Tutor/StudyMaterial/StudyMaterialRequestCreate";
import CartCommanTable, {
  type CartTableColumn,
} from "../../components/CommonTable/CartCommanTable";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Add, Remove } from "@mui/icons-material";
import { studyMaterialAddToCartCreate } from "../../../Redux/Slices/Booklet/Tutor/StudyMaterial/StudyMaterialAddToCartCreate";
import { PrinterOptions } from "../../../Redux/Slices/Booklet/Tutor/PrinterOptions/PrinterOptions";
import ConfirmDialog from "../../components/ComformDialog/ConformDialog";
import PrintIcon from "@mui/icons-material/Print";
import { Reprint } from "../../../Redux/Slices/Booklet/Tutor/Reprint/Reprint";
import { DefaultPrinter } from "../../../Redux/Slices/Booklet/Tutor/PrinterOptions/DefaultPrinter";
import { DashboardPrintlist } from "../../../Redux/Slices/Booklet/Tutor/PrinterOptions/DashboardPrintlist";
import { DefaultPrinterUpdate } from "../../../Redux/Slices/Booklet/Tutor/PrinterOptions/DefaultPrinterChange";

interface CartItem extends Record<string, unknown> {
  cart_id: string;
  pdf_id: string;
  tutor_id: number;
  quantity: number;
  created_on: string;
  updated_on: string;
  status: boolean;
  request_id?: string;
  pdf_data: {
    id: string;
    name: string;
    mimeType: string;
    webViewLink: string;
    modifiedTime: string;
    parents: string[];
  };
  [key: string]: unknown;
}

interface PrintForm {
  paperSize: string;
  side: string;
  staplePosition: string | null;
  color: string;
  orientation: string;
  scale: string;
  stapler: string;
  remark: string;
  pages_per_sheet: string;
  center_id?: string;
  printer_id?: string;
}

const RequestDetail: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data, isLoading } = useAppSelector((s) => s.BookletRequestReadSlice);

  const { data: printeroptions, isLoading: printeroptionsLoading } =
    useAppSelector((state) => state.PrinterOptionsSlice);

  const { data: Defaultprinteroptions } = useAppSelector(
    (state) => state.DefaultPrinterOptionsUpdate
  );

  const { data: PrinterList } = useAppSelector(
    (state) => state.DashbnoardPrintList
  );

  const location = useLocation();
  const request_id = location.state?.request_id;
  const isEdit = location.state?.isEdit ?? false;

  const [localCartItems, setLocalCartItems] = useState<CartItem[]>([]);
  const [cartItems, setCartItems] = useState<BookletCartItem[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [editPrinterOptions, setEditPrinterOptions] = useState(false);
  const [selectedPrinterError, setSelectedPrinterError] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState<string>("");

  const hasDefaultPrinter =
    Defaultprinteroptions?.status !== false &&
    Defaultprinteroptions?.status_code !== 404;

  const approval_status = data?.data.approval_status;

  // Initialize printForm with default values
  const [printForm, setPrintForm] = useState<PrintForm>({
    paperSize: "",
    side: "",
    staplePosition: null,
    color: "",
    orientation: "",
    scale: "",
    stapler: "NO",
    remark: "",
    pages_per_sheet: "",
    center_id: "",
    printer_id: "",
  });

  useEffect(() => {
    if (request_id) {
      dispatch(bookletRequestRead({ request_id }));
      dispatch(PrinterOptions());
    }
  }, [dispatch, request_id]);

  // when API data arrives, map to local state
  useEffect(() => {
    if (data?.data?.cart_items) {
      const mapped = mapCartItems(data.data.cart_items);
      setCartItems(mapped);
    }
  }, [data]);

  const cartItem = data?.data;
  const selectedCenter = cartItem?.center_details?.[0]?.center_id ?? null;
  const selectedPrinteroptions =
    cartItem?.printer_details?.[0]?.printer_id ?? null;

  useEffect(() => {
    if (selectedPrinteroptions) {
      setSelectedPrinter(selectedPrinteroptions.toString());
    }
  }, [selectedPrinteroptions]);

  useEffect(() => {
    if (selectedCenter) {
      dispatch(DashboardPrintlist({ center_id: selectedCenter }));
    }
  }, [cartItem, selectedCenter]);

  // Update printForm when data is available
  useEffect(() => {
    if (data?.data) {
      const cartItem = data.data;
      setPrintForm({
        paperSize: cartItem.papper_size?.data?.po_id || "",
        side: cartItem.side?.data?.po_id || "",
        staplePosition: cartItem.stapler_position?.data?.po_id || null,
        color: cartItem.color?.data?.po_id || "",
        orientation: cartItem.page_orientation?.data?.po_id || "",
        scale: cartItem.scale?.data?.po_id || "",
        stapler:
          cartItem.stapler?.data === true || cartItem.stapler?.data === "true"
            ? "YES"
            : "NO",
        remark: cartItem.tutor_remarks || "",
        pages_per_sheet: cartItem.pages_per_sheet?.data?.po_id || "",
      });
    }
  }, [data]);

  // Update printForm with default printer options when switch is ON
  useEffect(() => {
    if (editPrinterOptions && Defaultprinteroptions?.data) {
      const def = Defaultprinteroptions.data;

      setPrintForm({
        paperSize: def.papper_size || "",
        side: def.side || "",
        staplePosition: def.stapler_position || null,
        color: def.color || "",
        orientation: def.page_orientation || "",
        scale: def.scale || "",
        stapler: def.stapler ? "YES" : "NO",
        pages_per_sheet: def.pages_per_sheet || "",
        remark: "",
      });
    } else if (!editPrinterOptions && data?.data) {
      // If switch is OFF, reset to original API data
      const cartItem = data.data;
      setPrintForm({
        paperSize: cartItem.papper_size?.data?.po_id || "",
        side: cartItem.side?.data?.po_id || "",
        staplePosition: cartItem.stapler_position?.data?.po_id || null,
        color: cartItem.color?.data?.po_id || "",
        orientation: cartItem.page_orientation?.data?.po_id || "",
        scale: cartItem.scale?.data?.po_id || "",
        stapler:
          cartItem.stapler?.data === true || cartItem.stapler?.data === "true"
            ? "YES"
            : "NO",
        remark: cartItem.tutor_remarks || "",
        pages_per_sheet: cartItem.pages_per_sheet?.data?.po_id || "",
      });
    }
  }, [editPrinterOptions, Defaultprinteroptions]);

  const handlePrintChange = (field: keyof PrintForm, value: string) => {
    setPrintForm((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    setLocalCartItems(cartItems as any);
  }, [cartItems]);

  useEffect(() => {
    if (selectedPrinter) {
      dispatch(DefaultPrinterUpdate({ id: selectedPrinter }));
    }
  }, [selectedPrinter]);

  const mapCartItems = (items: any[]): BookletCartItem[] => {
    return items.map((item) => {
      const driveData = item.drive_data;
      const parents = driveData?.parents || [];

      // Use first subject name if multiple
      const subjectName = item.subject_data?.[0]?.name || "-";

      const classGroup =
        item.subject_data?.[0]?.yeargroup_data.year_group || "-";

      return {
        cart_id: item.cart_id,
        subject: subjectName,
        classGroup: classGroup || "-",
        topic: driveData?.name || "-",
        Previewid: driveData?.id || "-",
        fileType: driveData?.name || "-",
        lastUploadDate: driveData?.modifiedTime
          ? new Date(driveData.modifiedTime).toLocaleDateString()
          : "-",
        requestQty: item.quantity ?? 0,
      };
    });
  };

  const getDateChipProps = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return { label: "Invalid Date", color: "error" as const };
      }

      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

      if (diffInSeconds < 0) {
        return { label: "In the future", color: "info" as const };
      }

      if (diffInSeconds < 60) {
        return { label: "Just now", color: "success" as const };
      }

      if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return {
          label: minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`,
          color: "success" as const,
        };
      }

      if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return {
          label: hours === 1 ? "1 hour ago" : `${hours} hours ago`,
          color: "success" as const,
        };
      }

      if (diffInSeconds < 604800) {
        const days = Math.floor(diffInSeconds / 86400);
        return {
          label: days === 1 ? "Yesterday" : `${days} days ago`,
          color: "warning" as const,
        };
      }

      if (diffInSeconds < 2592000) {
        const weeks = Math.floor(diffInSeconds / 604800);
        return {
          label: weeks === 1 ? "1 week ago" : `${weeks} weeks ago`,
          color: "warning" as const,
        };
      }

      if (diffInSeconds < 31536000) {
        const months = Math.floor(diffInSeconds / 2592000);
        return {
          label: months === 1 ? "1 month ago" : `${months} months ago`,
          color: "default" as const,
        };
      }

      const years = Math.floor(diffInSeconds / 31536000);
      return {
        label: years === 1 ? "1 year ago" : `${years} years ago`,
        color: "default" as const,
      };
    } catch (error) {
      console.error("Error parsing date:", dateString, error);
      return { label: "Invalid Date", color: "error" as const };
    }
  };

  const options = printeroptions?.data ?? [];

  const optionsByCategory: Record<string, any[]> = Array.isArray(options)
    ? options.reduce((acc: Record<string, any[]>, option: any) => {
        if (!acc[option.category_id]) acc[option.category_id] = [];
        acc[option.category_id].push(option);
        return acc;
      }, {})
    : {};

  const handleQuantityChange = (
    row: BookletCartItem,
    index: number,
    newQty: number
  ) => {
    const validQty = Math.max(0, newQty);

    // Update state and remove zero-quantity items
    setCartItems((prev) => {
      const updated = prev.map((item) =>
        item.cart_id === row.cart_id ? { ...item, requestQty: validQty } : item
      );
      return updated.filter((item) => item.requestQty > 0);
    });

    const payload = {
      cart_id: row.cart_id,
      pdf_id: row.Previewid,
      tutor_id: data?.data?.tutor_details?.id,
      quantity: validQty,
      center_id: data?.data?.center_details?.[0].center_id,
      status: validQty === 0 ? false : true,
    };

    dispatch(studyMaterialAddToCartCreate(payload));
  };

  const columns: CartTableColumn<BookletCartItem>[] = [
    { key: "classGroup", title: "Year Group" },
    { key: "subject", title: "Subject" },
    { key: "fileType", title: "File name" },
    { key: "preview", title: "Preview" },
    {
      key: "created_at",
      title: "Last Upload Date",
      sortable: true,
      render: (_: unknown, row: any) => {
        const dateToFormat = row.lastUploadDate;
        if (!dateToFormat) {
          return <Chip label="Unknown" color="error" size="small" />;
        }

        const chipProps = getDateChipProps(dateToFormat);
        return <Chip {...chipProps} size="small" variant="outlined" />;
      },
    },
    {
      key: "quantity",
      title: "Request Qty",
      align: "center",
      render: (value, row, index) => {
        if (!isEdit) {
          return (
            <Typography textAlign="center" fontSize={"14px"} fontWeight={"500"}>
              {row.requestQty}
            </Typography>
          );
        }

        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
          >
            <IconButton
              size="small"
              sx={{
                bgcolor: "#009DFF",
                color: "white",
                "&:hover": { bgcolor: "#007ACC" },
              }}
              onClick={() =>
                handleQuantityChange(row as any, index, row.requestQty - 1)
              }
              aria-label="Decrease quantity"
            >
              <Remove fontSize="small" />
            </IconButton>

            <TextField
              value={row.requestQty}
              sx={{ width: { xs: "50px", sm: "70px" } }}
              onChange={(e) => {
                const newQty = parseInt(e.target.value, 10);
                if (!isNaN(newQty) && newQty >= 0) {
                  handleQuantityChange(row as any, index, newQty);
                }
              }}
              inputProps={{ min: 0, style: { textAlign: "center" } }}
              size="small"
            />

            <IconButton
              size="small"
              sx={{
                bgcolor: "#009DFF",
                color: "white",
                "&:hover": { bgcolor: "#007ACC" },
              }}
              onClick={() =>
                handleQuantityChange(row as any, index, row.requestQty + 1)
              }
              aria-label="Increase quantity"
            >
              <Add fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleReprint = async () => {
    if (!request_id) return;

    try {
      const response = await dispatch(Reprint({ request_id: request_id }));
      const message =
        (response as any)?.payload?.message || "Reprint request sent!";
      setDialogMessage(message);
      setOpenDialog(true);
    } catch (error: any) {
      console.error("Failed to reprint:", error);
      setDialogMessage(error?.message || "Failed to reprint request");
      setOpenDialog(true);
    }
  };

  console.log(selectedPrinteroptions, "SelectedCenter");

  const handleUpdate = async () => {
    const payload = {
      request_id: cartItem.request_id,
      status: true,
      tutor_remarks: printForm.remark,
      cart_ids: cartItems.map((item) => item.cart_id),
      papper_size: printForm.paperSize,
      side: printForm.side,
      page_orientation: printForm.orientation,
      scale: printForm.scale,
      stapler: printForm.stapler === "YES",
      stapler_position: printForm.staplePosition || null,
      color: printForm.color,
      pages_per_sheet: printForm.pages_per_sheet,
      center_id: selectedCenter,
      printer_id: selectedPrinter,
    };

    try {
      const response = await dispatch(studyMaterialRequestCreate(payload));
      const message =
        (response as any)?.payload?.message || "Request updated successfully!";
      setDialogMessage(message);
      setOpenDialog(true);
    } catch (error: any) {
      console.error("Failed to create request:", error);
      setDialogMessage(error?.message || "Failed to create request");
      setOpenDialog(true);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid sx={{ padding: "0px 24px", mb: 3 }}>
      {/* Tutor Details */}
      <Box>
        <Button
          sx={{
            fontSize: "16px",
            fontWeight: "400",
            textTransform: "none",
            marginBottom: "20px",
            p: 0,
            color: "black",
          }}
          onClick={() => navigate("/my_requests")}
        >
          <ArrowBackIcon />
          Back to Booklet Request
        </Button>
      </Box>
      <Box
        mb={3}
        sx={{
          fontSize: "16px",
        }}
      >
        <Typography fontSize={"20px"} fontWeight="500">
          Detail of Tutor
        </Typography>
        <Box display="flex" gap={6} mt={3} p={2} sx={{ fontSize: "14px" }}>
          <Box>
            <Typography fontWeight="500" fontSize={"14px"}>
              Center
            </Typography>
            <Typography fontSize={"14px"} marginTop={"6px"}>
              {data?.data?.center_details?.[0].center_name || "Center ANA"}
            </Typography>
          </Box>

          <Box>
            <Typography fontWeight="500" fontSize={"14px"}>
              Tutor Name
            </Typography>
            <Typography fontSize={"14px"} marginTop={"6px"}>
              {data?.data?.tutor_details?.name || "Unknown"}
            </Typography>
          </Box>

          <Box>
            <Typography fontWeight="500" fontSize={"14px"}>
              Request Date
            </Typography>
            <Typography fontSize={"14px"} marginTop={"6px"}>
              {data?.data?.created_at
                ? new Date(data?.data.created_at).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : ""}
            </Typography>
          </Box>

          <Box>
            <Typography fontSize={"14px"} fontWeight="500">
              Request Status
            </Typography>
            <Typography fontSize={"14px"} marginTop={"6px"}>
              {data?.data?.approval_status === "approved"
                ? "Approved"
                : data?.data?.approval_status === "rejected"
                ? "Rejected"
                : "Pending"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Booklet Details */}
      <Box p={2}>
        <Grid
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Typography fontSize={"20px"} fontWeight="500" mb={2}>
            Details of Booklet
          </Typography>
          {data?.data?.approval_status !== "pending" && (
            <Button
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                backgroundColor: "#009ddf",
                color: "white",
                textTransform: "none",
                padding: "6px 12px",
                borderRadius: "6px",
                fontWeight: 500,
                marginBottom: "10px",
                fontSize: "14px",
              }}
              onClick={handleReprint}
            >
              <PrintIcon style={{ fontSize: "18px" }} />
              Reprint
            </Button>
          )}
        </Grid>
        <CartCommanTable<BookletCartItem>
          loading={isLoading}
          columns={columns}
          data={cartItems}
          pageType="details"
          getPreviewId={(row) => row.Previewid}
        />

        <Typography
          mt={2}
          display="flex"
          justifyContent="flex-end"
          fontSize={"14px"}
          fontWeight={"500"}
          marginRight={"20px"}
        >
          Total Request Quantity:{" "}
          {cartItems.reduce(
            (sum: number, row: BookletCartItem) => sum + (row.requestQty || 0),
            0
          )}
        </Typography>
      </Box>
      <Box sx={{ mt: 3, mb: 1 }}>
        <Typography variant="h6" sx={{ fontSize: "16px" }} gutterBottom>
          Printer selection
        </Typography>

        <Box sx={{ maxWidth: "300px", mt: 2 }}>
          <FormControl fullWidth size="small">
            <FormLabel
              sx={{
                fontSize: "14px",
                color: "black",
                mb: 1,
                fontWeight: "400",
              }}
            >
              Select Printer{" "}
              <span style={{ color: "red", fontSize: "20px" }}>*</span>
            </FormLabel>

            <Select
              value={selectedPrinter}
              onChange={(e) => {
                setSelectedPrinter(e.target.value);
                setSelectedPrinterError(false);
              }}
              size="small"
              error={selectedPrinterError}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select Printer</em>
              </MenuItem>

              {PrinterList?.data?.map((printer: any) => (
                <MenuItem key={printer.printer_id} value={printer.printer_id}>
                  {printer.printer_details?.name} (
                  {printer.printer_details?.model})
                </MenuItem>
              ))}
            </Select>

            {selectedPrinterError && (
              <Typography variant="caption" color="error" fontSize={"12px"}>
                Please select a printer
              </Typography>
            )}
          </FormControl>
        </Box>
      </Box>

      {/* Printing Settings */}
      <Box sx={{ mt: 3, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "30px",
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "16px" }} gutterBottom>
            Printing Format Details
          </Typography>
          {data?.data?.approval_status === "pending" && (
            <FormControlLabel
              sx={{ fontSize: "12px", fontWeight: "500" }}
              label="Default printer options"
              control={
                <Switch
                  checked={editPrinterOptions}
                  disabled={!hasDefaultPrinter}
                  onChange={(e) => setEditPrinterOptions(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#009DDF",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#009DDF",
                      opacity: 1,
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: "#bdbdbd",
                      opacity: 1,
                    },
                    "& .MuiSwitch-thumb": {
                      backgroundColor: "#757575",
                    },
                  }}
                />
              }
            />
          )}
        </Box>
        <Box
          sx={{
            opacity: !editPrinterOptions ? 1 : 0.5,
            pointerEvents: !editPrinterOptions ? "auto" : "none",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(auto-fit, minmax(165px, 1fr))",
              },
              gap: { xs: "20px", md: "30px 40px" },
              width: "100%",
              maxWidth: "1060px",
              mt: 2,
            }}
          >
            {/* Paper Size */}
            <FormControl fullWidth size="small">
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Paper Size
              </FormLabel>
              <Select
                value={printForm.paperSize}
                onChange={(e) => handlePrintChange("paperSize", e.target.value)}
                size="small"
                disabled={!isEdit}
                sx={{
                  color: "black",
                  "& .MuiSelect-select.Mui-disabled": {
                    WebkitTextFillColor: "black",
                  },
                }}
              >
                {optionsByCategory["CAT001"]?.map((opt: any) => (
                  <MenuItem key={opt.po_id} value={opt.po_id}>
                    {opt.printer_option_value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Side */}
            <FormControl fullWidth size="small">
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Side
              </FormLabel>
              <Select
                value={printForm.side}
                onChange={(e) => handlePrintChange("side", e.target.value)}
                size="small"
                disabled={!isEdit}
                sx={{
                  color: "black",
                  "& .MuiSelect-select.Mui-disabled": {
                    WebkitTextFillColor: "black",
                  },
                }}
              >
                {optionsByCategory["CAT002"]?.map((opt: any) => (
                  <MenuItem key={opt.po_id} value={opt.po_id}>
                    {opt.printer_option_value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Black & White / Color */}
            <FormControl fullWidth size="small">
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Black and White/Color
              </FormLabel>
              <Select
                value={printForm.color}
                onChange={(e) => handlePrintChange("color", e.target.value)}
                size="small"
                disabled={!isEdit}
                sx={{
                  color: "black",
                  "& .MuiSelect-select.Mui-disabled": {
                    WebkitTextFillColor: "black",
                  },
                }}
              >
                {optionsByCategory["CAT007"]?.map((opt: any) => (
                  <MenuItem key={opt.po_id} value={opt.po_id}>
                    {opt.printer_option_value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl component="fieldset" size="small">
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Page Orientation
              </FormLabel>
              <RadioGroup
                row
                value={printForm.orientation}
                onChange={(e) =>
                  handlePrintChange("orientation", e.target.value)
                }
              >
                {optionsByCategory["CAT003"]?.map((opt: any) => (
                  <FormControlLabel
                    key={opt.po_id}
                    value={opt.po_id}
                    control={<Radio size="small" disabled={!isEdit} />}
                    label={opt.printer_option_value}
                    sx={{
                      "& .MuiFormControlLabel-label.Mui-disabled": {
                        color: "black",
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            {/* Scale */}
            <FormControl fullWidth size="small">
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Scale
              </FormLabel>
              <Select
                value={printForm.scale}
                onChange={(e) => handlePrintChange("scale", e.target.value)}
                size="small"
                disabled={!isEdit}
                sx={{
                  color: "black",
                  "& .MuiSelect-select.Mui-disabled": {
                    WebkitTextFillColor: "black",
                  },
                }}
              >
                {optionsByCategory["CAT004"]?.map((opt: any) => (
                  <MenuItem key={opt.po_id} value={opt.po_id}>
                    {opt.printer_option_value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Stapler Printing */}
            <FormControl component="fieldset" size="small">
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Stapler Printing
              </FormLabel>
              <RadioGroup
                row
                value={printForm.stapler}
                sx={{ color: "green" }}
                onChange={(e) => handlePrintChange("stapler", e.target.value)}
              >
                <FormControlLabel
                  value="YES"
                  control={<Radio size="small" disabled={!isEdit} />}
                  label="Yes"
                  sx={{
                    "& .MuiFormControlLabel-label.Mui-disabled": {
                      color: "black",
                    },
                  }}
                />
                <FormControlLabel
                  value="NO"
                  control={<Radio size="small" disabled={!isEdit} />}
                  label="No"
                  sx={{
                    "& .MuiFormControlLabel-label.Mui-disabled": {
                      color: "black",
                    },
                  }}
                />
              </RadioGroup>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              disabled={printForm.stapler !== "YES" || !isEdit}
            >
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Staple Position
              </FormLabel>
              <Select
                value={printForm.staplePosition || ""}
                onChange={(e) =>
                  handlePrintChange("staplePosition", e.target.value || "")
                }
                size="small"
                sx={{
                  color: "black",
                  "& .MuiSelect-select.Mui-disabled": {
                    WebkitTextFillColor: "black",
                  },
                }}
              >
                {optionsByCategory["CAT006"]?.map((opt: any) => (
                  <MenuItem key={opt.po_id} value={opt.po_id}>
                    {opt.printer_option_value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
                Page per sheet
              </FormLabel>
              <Select
                value={printForm.pages_per_sheet}
                onChange={(e) =>
                  handlePrintChange("pages_per_sheet", e.target.value || "")
                }
                size="small"
                disabled={!isEdit}
                sx={{
                  color: "black",
                  "& .MuiSelect-select.Mui-disabled": {
                    WebkitTextFillColor: "black",
                  },
                }}
              >
                {optionsByCategory["CAT008"]?.map((opt: any) => (
                  <MenuItem key={opt.po_id} value={opt.po_id}>
                    {opt.printer_option_value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* Remarks */}
      <Box mt={3} p={2}>
        <Typography mb={1} fontSize="14px">
          Tutor Remark
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={printForm.remark}
          onChange={(e) => handlePrintChange("remark", e.target.value)}
          disabled={!isEdit}
          InputProps={{
            sx: {
              fontSize: "14px",
              maxWidth: "600px",
              backgroundColor: !isEdit ? "#f5f5f5" : "white",
              borderRadius: "6px",
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "rgba(0,0,0,0.87)",
              },
            },
          }}
        />

        <>
          {approval_status !== "pending" && (
            <>
              <Typography mt={2} mb={1} fontSize="14px">
                Admin Remark
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                value={data?.data?.admin_remarks || ""}
                disabled
                InputProps={{
                  sx: {
                    fontSize: "14px",
                    maxWidth: "600px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "6px",
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "rgba(0,0,0,0.87)",
                    },
                  },
                }}
              />
            </>
          )}
        </>
      </Box>

      {/* Edit Mode Actions */}
      {isEdit && (
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            sx={{
              color: "#0a0a0aff",
              borderColor: "none",
              fontSize: "14px",
              textTransform: "none",
              borderRadius: "20px",
            }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            sx={{
              fontSize: "14px",
              color: "#ffffff",
              bgcolor: "#009DFF",
              borderRadius: "20px",
              textTransform: "none",
              "&:hover": {
                opacity: 0.8,
                bgcolor: "#009DFF",
              },
            }}
            onClick={handleUpdate}
          >
            Update
          </Button>
        </Box>
      )}

      <ConfirmDialog
        open={openDialog}
        message={dialogMessage}
        onClose={() => setOpenDialog(false)}
        onConfirm={() => {
          setOpenDialog(false);
          navigate("/my_requests");
        }}
      />
    </Grid>
  );
};

export default RequestDetail;
